<template>
  <div class="key-management">
    <h1>密钥管理</h1>
    
    <!-- 管理员功能区 -->
    <div class="admin-section">
      <div class="header-actions">
        <button @click="showGenerateModal" class="generate-btn">
          <i class="fas fa-plus"></i> 生成密钥
        </button>
        <button @click="showBatchGenerateModal" class="batch-generate-btn">
          <i class="fas fa-layer-group"></i> 批量生成
        </button>
      </div>

      <!-- 筛选器 -->
      <div class="filters">
        <select v-model="statusFilter" @change="fetchKeys">
          <option value="all">全部状态</option>
          <option value="active">未使用</option>
          <option value="used">已使用</option>
        </select>
      </div>

      <!-- 密钥列表 -->
      <div class="key-list">
        <div v-if="isLoading" class="loading">
          <div class="spinner"></div>
          <p>加载中...</p>
        </div>
        
        <div v-else-if="error" class="error-message">
          {{ error }}
          <button @click="fetchKeys" class="retry-btn">重试</button>
        </div>
        
        <div v-else-if="keys.length === 0" class="empty-state">
          <i class="fas fa-key"></i>
          <p>暂无密钥</p>
        </div>
        
        <div v-else class="key-table">
          <table>
            <thead>
              <tr>
                <th>密钥代码</th>
                <th>增加次数</th>
                <th>状态</th>
                <th>备注</th>
                <th>使用者</th>
                <th>使用时间</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="key in keys" :key="key.id">
                <td class="key-code">
                  <span class="code-text">{{ maskKeyCode(key.key_code) }}</span>
                  <button @click="toggleKeyVisibility(key.id)" class="toggle-btn">
                    <i :class="visibleKeys[key.id] ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                  </button>
                </td>
                <td>{{ key.add_count }}</td>
                <td>
                  <span :class="getStatusClass(key)" class="status-badge">
                    {{ getStatusText(key) }}
                  </span>
                </td>
                <td>{{ key.remark || '-' }}</td>
                <td>{{ key.used_by || '-' }}</td>
                <td>{{ key.used_at ? formatDate(key.used_at) : '-' }}</td>
                <td>{{ formatDate(key.created_at) }}</td>
                <td class="actions">
                  <button @click="editKey(key)" class="edit-btn" :disabled="!key.is_active">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button @click="deleteKey(key.id)" class="delete-btn">
                    <i class="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 分页 -->
        <div v-if="totalPages > 1" class="pagination">
          <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="page-btn">
            上一页
          </button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages" class="page-btn">
            下一页
          </button>
        </div>
      </div>
    </div>

    <!-- 用户使用密钥区域 -->
    <div class="user-section">
      <h2>使用密钥</h2>
      <div class="use-key-form">
        <input 
          v-model="useKeyCode" 
          type="text" 
          placeholder="请输入密钥代码"
          class="key-input"
        >
        <button @click="useKey" :disabled="isUsingKey || !useKeyCode.trim()" class="use-btn">
          {{ isUsingKey ? '使用中...' : '使用密钥' }}
        </button>
      </div>
    </div>

    <!-- 生成密钥弹窗 -->
    <div v-if="showGenerateKeyModal" class="modal-overlay" @click="closeGenerateModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>生成密钥</h3>
          <button class="close-btn" @click="closeGenerateModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="generateKey">
            <div class="form-group">
              <label for="addCount">增加次数</label>
              <input 
                id="addCount"
                v-model.number="generateForm.add_count" 
                type="number" 
                min="1"
                placeholder="请输入增加次数"
                required
              >
            </div>
            <div class="form-group">
              <label for="remark">备注</label>
              <input 
                id="remark"
                v-model="generateForm.remark" 
                type="text" 
                placeholder="请输入备注（可选）"
              >
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeGenerateModal">取消</button>
          <button 
            class="confirm-btn" 
            :disabled="isGenerating" 
            @click="generateKey"
          >
            {{ isGenerating ? '生成中...' : '生成密钥' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 批量生成密钥弹窗 -->
    <div v-if="showBatchGenerateKeyModal" class="modal-overlay" @click="closeBatchGenerateModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>批量生成密钥</h3>
          <button class="close-btn" @click="closeBatchGenerateModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="batchGenerateKeys">
            <div class="form-group">
              <label for="count">生成数量</label>
              <input 
                id="count"
                v-model.number="batchGenerateForm.count" 
                type="number" 
                min="1"
                max="100"
                placeholder="请输入生成数量（1-100）"
                required
              >
            </div>
            <div class="form-group">
              <label for="batchAddCount">增加次数</label>
              <input 
                id="batchAddCount"
                v-model.number="batchGenerateForm.add_count" 
                type="number" 
                min="1"
                placeholder="请输入增加次数"
                required
              >
            </div>
            <div class="form-group">
              <label for="batchRemark">备注</label>
              <input 
                id="batchRemark"
                v-model="batchGenerateForm.remark" 
                type="text" 
                placeholder="请输入备注（可选）"
              >
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeBatchGenerateModal">取消</button>
          <button 
            class="confirm-btn" 
            :disabled="isBatchGenerating" 
            @click="batchGenerateKeys"
          >
            {{ isBatchGenerating ? '生成中...' : '批量生成' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 编辑密钥弹窗 -->
    <div v-if="showEditKeyModal" class="modal-overlay" @click="closeEditModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>编辑密钥</h3>
          <button class="close-btn" @click="closeEditModal">&times;</button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="updateKey">
            <div class="form-group">
              <label for="editAddCount">增加次数</label>
              <input 
                id="editAddCount"
                v-model.number="editForm.add_count" 
                type="number" 
                min="1"
                required
              >
            </div>
            <div class="form-group">
              <label for="editRemark">备注</label>
              <input 
                id="editRemark"
                v-model="editForm.remark" 
                type="text" 
                placeholder="请输入备注（可选）"
              >
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input 
                  v-model="editForm.is_active" 
                  type="checkbox"
                >
                激活状态
              </label>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeEditModal">取消</button>
          <button 
            class="confirm-btn" 
            :disabled="isUpdating" 
            @click="updateKey"
          >
            {{ isUpdating ? '更新中...' : '更新密钥' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from '../config/api';

export default {
  name: 'KeyManagement',
  data() {
    return {
      keys: [],
      isLoading: false,
      error: null,
      statusFilter: 'all',
      currentPage: 1,
      totalPages: 1,
      visibleKeys: {},
      
      // 使用密钥
      useKeyCode: '',
      isUsingKey: false,
      
      // 生成密钥
      showGenerateKeyModal: false,
      isGenerating: false,
      generateForm: {
        add_count: 10,
        remark: ''
      },
      
      // 批量生成密钥
      showBatchGenerateKeyModal: false,
      isBatchGenerating: false,
      batchGenerateForm: {
        count: 5,
        add_count: 10,
        remark: ''
      },
      
      // 编辑密钥
      showEditKeyModal: false,
      isUpdating: false,
      editingKeyId: null,
      editForm: {
        add_count: 10,
        remark: '',
        is_active: true
      }
    }
  },
  created() {
    this.fetchKeys();
  },
  methods: {
    async fetchKeys() {
      try {
        this.isLoading = true;
        this.error = null;
        
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/admin/keys?page=${this.currentPage}&limit=20&status=${this.statusFilter}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('权限不足，需要管理员权限');
          }
          throw new Error('获取密钥列表失败');
        }

        const data = await response.json();
        this.keys = data.keys || [];
        this.totalPages = Math.ceil((data.total || 0) / 20);
      } catch (error) {
        console.error('获取密钥列表失败:', error);
        this.error = error.message;
      } finally {
        this.isLoading = false;
      }
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchKeys();
      }
    },

    toggleKeyVisibility(keyId) {
      this.visibleKeys = {
        ...this.visibleKeys,
        [keyId]: !this.visibleKeys[keyId]
      };
    },

    maskKeyCode(keyCode) {
      if (!keyCode) return '';
      
      const key = this.keys.find(k => k.key_code === keyCode);
      if (this.visibleKeys[key?.id]) {
        return keyCode;
      }
      
      if (keyCode.length <= 12) {
        return '*'.repeat(keyCode.length);
      }
      return keyCode.substring(0, 8) + '*'.repeat(keyCode.length - 12) + keyCode.substring(keyCode.length - 4);
    },

    getStatusClass(key) {
      if (!key.is_active) return 'status-used';
      return 'status-active';
    },

    getStatusText(key) {
      if (!key.is_active) return '已使用';
      return '未使用';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-CN');
    },

    // 生成密钥相关方法
    showGenerateModal() {
      this.generateForm = {
        add_count: 10,
        remark: ''
      };
      this.showGenerateKeyModal = true;
    },

    closeGenerateModal() {
      this.showGenerateKeyModal = false;
    },

    async generateKey() {
      try {
        this.isGenerating = true;
        
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/admin/keys`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            add_count: this.generateForm.add_count,
            remark: this.generateForm.remark
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || '生成密钥失败');
        }

        await this.fetchKeys();
        this.closeGenerateModal();
        this.showSuccessMessage('密钥生成成功');
      } catch (error) {
        console.error('生成密钥失败:', error);
        this.error = error.message;
      } finally {
        this.isGenerating = false;
      }
    },

    // 批量生成密钥相关方法
    showBatchGenerateModal() {
      this.batchGenerateForm = {
        count: 5,
        add_count: 10,
        remark: ''
      };
      this.showBatchGenerateKeyModal = true;
    },

    closeBatchGenerateModal() {
      this.showBatchGenerateKeyModal = false;
    },

    async batchGenerateKeys() {
      try {
        this.isBatchGenerating = true;
        
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/admin/keys/batch`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            count: this.batchGenerateForm.count,
            add_count: this.batchGenerateForm.add_count,
            remark: this.batchGenerateForm.remark
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || '批量生成密钥失败');
        }

        await this.fetchKeys();
        this.closeBatchGenerateModal();
        this.showSuccessMessage(`成功生成 ${this.batchGenerateForm.count} 个密钥`);
      } catch (error) {
        console.error('批量生成密钥失败:', error);
        this.error = error.message;
      } finally {
        this.isBatchGenerating = false;
      }
    },

    // 编辑密钥相关方法
    editKey(key) {
      this.editingKeyId = key.id;
      this.editForm = {
        add_count: key.add_count,
        remark: key.remark || '',
        is_active: key.is_active
      };
      this.showEditKeyModal = true;
    },

    closeEditModal() {
      this.showEditKeyModal = false;
      this.editingKeyId = null;
    },

    async updateKey() {
      try {
        this.isUpdating = true;
        
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/admin/keys/${this.editingKeyId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.editForm)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || '更新密钥失败');
        }

        await this.fetchKeys();
        this.closeEditModal();
        this.showSuccessMessage('密钥更新成功');
      } catch (error) {
        console.error('更新密钥失败:', error);
        this.error = error.message;
      } finally {
        this.isUpdating = false;
      }
    },

    async deleteKey(keyId) {
      if (!confirm('确定要删除这个密钥吗？')) {
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/admin/keys/${keyId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || '删除密钥失败');
        }

        await this.fetchKeys();
        this.showSuccessMessage('密钥删除成功');
      } catch (error) {
        console.error('删除密钥失败:', error);
        this.error = error.message;
      }
    },

    // 使用密钥
    async useKey() {
      if (!this.useKeyCode.trim()) {
        this.error = '请输入密钥代码';
        return;
      }

      try {
        this.isUsingKey = true;
        this.error = null;
        
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/user/use-key`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            key_code: this.useKeyCode.trim()
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || '使用密钥失败');
        }

        const data = await response.json();
        this.useKeyCode = '';
        this.showSuccessMessage(`密钥使用成功！增加了 ${data.added_count} 次查询次数，当前总次数：${data.total_count}`);
        
        // 刷新密钥列表
        await this.fetchKeys();
      } catch (error) {
        console.error('使用密钥失败:', error);
        this.error = error.message;
      } finally {
        this.isUsingKey = false;
      }
    },

    showSuccessMessage(message) {
      alert(message);
    }
  }
}
</script>

<style scoped>
.key-management {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

h1 {
  color: #2c3e50;
  margin-bottom: 30px;
  font-size: 28px;
}

h2 {
  color: #2c3e50;
  margin-bottom: 20px;
  font-size: 22px;
}

.admin-section {
  margin-bottom: 40px;
}

.header-actions {
  margin-bottom: 20px;
  display: flex;
  gap: 12px;
}

.generate-btn, .batch-generate-btn {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.generate-btn:hover, .batch-generate-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.4);
}

.filters {
  margin-bottom: 20px;
}

.filters select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.loading {
  text-align: center;
  padding: 40px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4facfe;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  background-color: #fee;
  color: #c33;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 20px;
}

.retry-btn {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.key-table {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.key-table table {
  width: 100%;
  border-collapse: collapse;
}

.key-table th,
.key-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
}

.key-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
}

.key-code {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Courier New', monospace;
}

.code-text {
  flex: 1;
  word-break: break-all;
}

.toggle-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.toggle-btn:hover {
  color: #495057;
  background-color: #e9ecef;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-active {
  background-color: #d4edda;
  color: #155724;
}

.status-used {
  background-color: #f8d7da;
  color: #721c24;
}

.actions {
  display: flex;
  gap: 8px;
}

.edit-btn, .delete-btn {
  background: none;
  border: none;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.edit-btn {
  color: #007bff;
}

.edit-btn:hover:not(:disabled) {
  background-color: #e3f2fd;
}

.edit-btn:disabled {
  color: #6c757d;
  cursor: not-allowed;
}

.delete-btn {
  color: #dc3545;
}

.delete-btn:hover {
  background-color: #ffebee;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
}

.page-btn {
  background: #4facfe;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: #3d8bfe;
}

.page-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: #2c3e50;
}

.user-section {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.use-key-form {
  display: flex;
  gap: 12px;
  align-items: center;
}

.key-input {
  flex: 1;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  font-family: 'Courier New', monospace;
}

.use-btn {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.use-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
}

.use-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  color: #2c3e50;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #2c3e50;
}

.checkbox-label {
  display: flex !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

.form-group input[type="checkbox"] {
  width: auto;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #4facfe;
  box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e9ecef;
}

.cancel-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.cancel-btn:hover {
  background: #5a6268;
}

.confirm-btn {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.confirm-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.4);
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>